import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Row, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Line } from '@ant-design/charts';
import dayjs from 'dayjs';
import type { GroupAnalyticsParams, SessionStatsRow } from '@/types';
import { adminApi } from '@services/api';
import { reportLoadErrorMessage } from '@/utils/apiError';

const { Text } = Typography;

const SERIES_DOMAIN = ['Median', 'P90'];
const SERIES_COLORS = ['#1677ff', '#fa8c16'];

const fmtDuration = (seconds: number): string => {
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

const fmtPct = (share: number): string => `${(share * 100).toFixed(1)}%`;

const fmtDay = (day: string): string => dayjs(day).format('D MMM YYYY');

/**
 * A day where no session has a measured length (none closed with
 * session_end): the percentile falls back to 0. That 0 is missing data, not
 * a zero-second session.
 */
const hasMeasuredLength = (r: SessionStatsRow): boolean =>
  r.sessions > 0 && r.p90Seconds > 0 && (r.timeoutShare ?? 0) < 1;

/**
 * Sessions per IST day and how long they last.
 *
 * The share with no measured length leads (the API still calls it
 * timeoutShare) because it decides whether the lengths mean anything:
 * sessions the app didn't close with session_end, closed by the server or by
 * the player's next session, never get a length, so a high share leaves the
 * median and P90 describing only the sessions that closed cleanly.
 */
export const SessionStatsCard: React.FC<{ params: GroupAnalyticsParams }> = ({ params }) => {
  const [rows, setRows] = useState<SessionStatsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getSessionStats(params);
      setRows(res.data ?? []);
      setError(null);
    } catch (e) {
      setError(reportLoadErrorMessage(e, 'Could not load session stats'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = useMemo(() => {
    const totalSessions = rows.reduce((s, r) => s + r.sessions, 0);

    const withShare = rows.filter((r) => r.timeoutShare !== undefined);
    const shareSessions = withShare.reduce((s, r) => s + r.sessions, 0);
    const timeoutShare =
      shareSessions > 0
        ? withShare.reduce((s, r) => s + (r.timeoutShare ?? 0) * r.sessions, 0) / shareSessions
        : null;

    const measured = rows.filter(hasMeasuredLength);
    const measuredSessions = measured.reduce((s, r) => s + r.sessions, 0);
    const avgDailyMedian =
      measuredSessions > 0
        ? measured.reduce((s, r) => s + r.medianSeconds * r.sessions, 0) / measuredSessions
        : null;

    return {
      totalSessions,
      timeoutShare,
      avgDailyMedian,
      unmeasuredDays: rows.length - measured.length,
    };
  }, [rows]);

  // Days without a measured length are left out rather than plotted as 0.
  const chartData = useMemo(
    () =>
      rows.filter(hasMeasuredLength).flatMap((r) => {
        const day = dayjs(r.day).format('D MMM');
        return [
          { day, series: 'Median', minutes: Math.round(r.medianSeconds / 6) / 10 },
          { day, series: 'P90', minutes: Math.round(r.p90Seconds / 6) / 10 },
        ];
      }),
    [rows]
  );

  const columns = useMemo<ColumnsType<SessionStatsRow>>(
    () => [
      {
        title: 'Day',
        dataIndex: 'day',
        key: 'day',
        render: (d: string) => fmtDay(d),
        sorter: (a, b) => a.day.localeCompare(b.day),
        defaultSortOrder: 'descend',
      },
      {
        title: 'Sessions',
        dataIndex: 'sessions',
        key: 'sessions',
        align: 'right',
        render: (v: number) => v.toLocaleString('en-IN'),
        sorter: (a, b) => a.sessions - b.sessions,
      },
      {
        title: 'Users',
        dataIndex: 'users',
        key: 'users',
        align: 'right',
        render: (v: number) => v.toLocaleString('en-IN'),
        sorter: (a, b) => a.users - b.users,
      },
      {
        title: 'Sessions/user',
        dataIndex: 'sessionsPerUser',
        key: 'sessionsPerUser',
        align: 'right',
        render: (v?: number) => (v === undefined ? '—' : v.toFixed(2)),
        sorter: (a, b) => (a.sessionsPerUser ?? -1) - (b.sessionsPerUser ?? -1),
      },
      {
        title: 'Median',
        dataIndex: 'medianSeconds',
        key: 'medianSeconds',
        align: 'right',
        render: (v: number, r) => (hasMeasuredLength(r) ? fmtDuration(v) : '—'),
        sorter: (a, b) =>
          (hasMeasuredLength(a) ? a.medianSeconds : -1) -
          (hasMeasuredLength(b) ? b.medianSeconds : -1),
      },
      {
        title: 'P90',
        dataIndex: 'p90Seconds',
        key: 'p90Seconds',
        align: 'right',
        render: (v: number, r) => (hasMeasuredLength(r) ? fmtDuration(v) : '—'),
        sorter: (a, b) =>
          (hasMeasuredLength(a) ? a.p90Seconds : -1) - (hasMeasuredLength(b) ? b.p90Seconds : -1),
      },
      {
        title: 'Matches/session',
        dataIndex: 'matchesPerSession',
        key: 'matchesPerSession',
        align: 'right',
        render: (v?: number) => (v === undefined ? '—' : v.toFixed(2)),
        sorter: (a, b) => (a.matchesPerSession ?? -1) - (b.matchesPerSession ?? -1),
      },
      {
        title: 'No measured length',
        dataIndex: 'timeoutShare',
        key: 'timeoutShare',
        align: 'right',
        render: (v?: number) =>
          v === undefined ? '—' : <Text type={v > 0.5 ? 'danger' : undefined}>{fmtPct(v)}</Text>,
        sorter: (a, b) => (a.timeoutShare ?? -1) - (b.timeoutShare ?? -1),
      },
    ],
    []
  );

  const highTimeout = summary.timeoutShare !== null && summary.timeoutShare > 0.5;

  return (
    <Card
      loading={loading}
      title={
        <span>
          Sessions{' '}
          <Tooltip title="Lengths are measured by the server, not reported by the client. Sessions that end without a session_end (crash, app kill) are closed by the server or by the player's next session; their length is unknown, so they are left out of the median and P90 and count as 0 matches. Read the share with no measured length first — when it is high, the lengths describe only the sessions that closed cleanly, not typical play. Only collects since session recording began.">
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </span>
      }
    >
      {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />}

      {rows.length === 0 ? (
        !error && (
          <Text type="secondary">
            No closed sessions in this range. Each day needs sessions that have ended, either with a
            session_end from the client or closed by the server or the player's next session, and
            data only exists from when session recording began.
          </Text>
        )
      ) : (
        <>
          {summary.timeoutShare !== null && (
            <div style={{ marginBottom: 12 }}>
              <Tag color={highTimeout ? 'red' : 'default'}>
                {fmtPct(summary.timeoutShare)} with no measured length
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {highTimeout
                  ? 'Most sessions never sent session_end, so the lengths below cover only the minority that closed cleanly.'
                  : 'The rest closed with a session_end, so the lengths below cover most sessions.'}
              </Text>
            </div>
          )}

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12}>
              <Statistic
                title="Total sessions"
                value={summary.totalSessions}
                formatter={(v) => Number(v).toLocaleString('en-IN')}
              />
            </Col>
            <Col xs={12}>
              <Statistic
                title="Avg daily median"
                value={summary.avgDailyMedian === null ? '—' : fmtDuration(summary.avgDailyMedian)}
              />
            </Col>
          </Row>

          {chartData.length > 0 && (
            <Line
              data={chartData}
              xField="day"
              yField="minutes"
              colorField="series"
              height={220}
              legend={{ color: { position: 'top' } }}
              scale={{ color: { domain: SERIES_DOMAIN, range: SERIES_COLORS } }}
              axis={{ x: { labelAutoHide: true }, y: { title: 'Minutes' } }}
            />
          )}

          {summary.unmeasuredDays > 0 && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
              {summary.unmeasuredDays} day{summary.unmeasuredDays === 1 ? '' : 's'} had no session
              with a measured length and {summary.unmeasuredDays === 1 ? 'is' : 'are'} left out of
              the chart and the average.
            </Text>
          )}

          <Table<SessionStatsRow>
            size="small"
            rowKey="day"
            dataSource={rows}
            columns={columns}
            pagination={rows.length > 15 ? { pageSize: 10, showSizeChanger: false } : false}
            style={{ marginTop: 12 }}
          />
        </>
      )}
    </Card>
  );
};
